import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { ExportApiService, PdfExportFlags } from '../services/export-api.service';
import { ChartCaptureService } from '../services/chart-capture.service';

@Component({
  selector: 'app-export-project',
  templateUrl: './export-project.component.html',
  styleUrls: ['./export-project.component.scss'],
})
export class ExportProjectComponent implements OnInit, OnChanges {
  @Input() projectName: string = '';
  @Input() tenantId: string = '';
  @Input() unitCode: number = 0;
  @Input() exportMode: 'pdf' | 'csv' = 'pdf';

  @Output() close = new EventEmitter<void>();

  mode: 'pdf' | 'csv' = 'pdf';
  isGenerating = false;
  errorMessage = '';

  flags: PdfExportFlags = {
    Zones: true,
    UserParameters: true,
    CuringGraph: true,
    Graph3D: true,
    TemperatureOverTime: true,
    Notes: true,
    Log: true,
  };

  readonly sections: Array<{ key: keyof PdfExportFlags; label: string }> = [
    { key: 'Zones', label: 'Zones' },
    { key: 'UserParameters', label: 'User Parameters' },
    { key: 'CuringGraph', label: 'Curing Graph' },
    { key: 'Graph3D', label: '3D Graph' },
    { key: 'TemperatureOverTime', label: 'Temperature Over Time' },
    { key: 'Notes', label: 'Notes' },
    { key: 'Log', label: 'Fiber Fault Log' },
  ];

  constructor(private exportApi: ExportApiService, private chartCapture: ChartCaptureService) {}

  ngOnInit(): void {
    this.mode = this.exportMode;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['exportMode']) {
      this.mode = this.exportMode;
      this.errorMessage = '';
    }
  }

  setMode(m: 'pdf' | 'csv'): void {
    this.mode = m;
    this.errorMessage = '';
  }

  generate(): void {
    if (!this.projectName) return;
    this.isGenerating = true;
    this.errorMessage = '';

    if (this.mode === 'csv') {
      this.exportApi
        .generateCSV(this.projectName, this.tenantId, this.unitCode)
        .subscribe({
          next: (blob) => {
            const filename = `${this.projectName}_${this.formatDate()}.csv`;
            this.exportApi.downloadBlob(blob, filename);
            this.isGenerating = false;
          },
          error: () => {
            this.errorMessage = 'Failed to generate CSV. Please try again.';
            this.isGenerating = false;
          },
        });
    } else {
      this.chartCapture
        .captureAllForExport([
          'curingGraph',
          'zones',
          'heatmap3D',
          'temperatureOverTime',
        ])
        .then((chartImages) => {
          this.exportApi
            .generatePDF(this.projectName, this.tenantId, this.unitCode, this.flags, chartImages)
            .subscribe({
              next: (blob) => {
                const filename = `${this.projectName}_${this.formatDate()}.pdf`;
                this.exportApi.downloadBlob(blob, filename);
                this.isGenerating = false;
              },
              error: () => {
                this.errorMessage = 'Failed to generate PDF. Please try again.';
                this.isGenerating = false;
              },
            });
        })
        .catch(() => {
          this.errorMessage = 'Failed to capture chart images. Please try again.';
          this.isGenerating = false;
        });
    }
  }

  goBack(): void {
    this.close.emit();
  }

  private formatDate(): string {
    const now = new Date();
    return now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
  }
}
